import { useParams, Link, Navigate } from "react-router-dom";
import { Phone, CheckCircle2, ArrowRight, AlertTriangle, ShieldCheck, XCircle, BookOpen, ExternalLink, Sparkles } from "lucide-react";
import type { ReactNode } from "react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { PageSEO } from "@/components/site/PageSEO";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { CTASection } from "@/components/site/CTASection";
import { TrustStrip } from "@/components/site/TrustStrip";
import { ServiceLeadForm } from "@/components/site/ServiceLeadForm";
import { HeroBackground } from "@/components/site/HeroBackground";
import { ClientStoryBlock } from "@/components/site/narrative/ClientStory";
import { getServiceDetail, serviceCategories } from "@/data/site/services";
import { getServiceRich } from "@/data/site/serviceRichContent";
import { brand, founder } from "@/data/site/brand";
import { clientStories, type ClientStory } from "@/data/site/clientStories";
import { stockImages, type StockImageKey } from "@/data/site/stockImages";

const serviceHeroImage: Record<string, StockImageKey> = {
  "wage-garnishment": "countingCash",
  "bank-levy": "countingCash",
  "offer-in-compromise": "piggyBank",
  "back-taxes": "taxFormsCalculator",
  "irs-audit-representation": "signingForm",
  "payroll-tax": "accountingDesk",
  "innocent-spouse-relief": "consultation",
  "unfiled-returns": "taxFormsStack",
  "tax-lien-help": "financialChart",
};

const pickStoryForService = (categorySlug: string, serviceSlug: string): ClientStory => {
  const map: Record<string, ClientStory["topics"][number]> = {
    "wage-garnishment": "wage-garnishment",
    "bank-levy": "bank-levy",
    "offer-in-compromise": "offer-in-compromise",
    "back-taxes": "back-taxes",
    "irs-audit-representation": "audit",
    "payroll-tax": "payroll-tax",
    "innocent-spouse-relief": "innocent-spouse",
    "unfiled-returns": "unfiled-returns",
    "tax-lien-help": "lien",
  };
  const topic = map[serviceSlug];
  const match = topic && clientStories.find((s) => s.topics.includes(topic));
  return match ?? clientStories[0];
};

/**
 * AEO-optimized section. The H2 is a question, and the `answer` prop is the
 * 1–2 sentence speakable direct answer that sits immediately under it — the
 * exact pattern Google AI Overviews, Perplexity, and ChatGPT extract.
 * The deeper body content (lists, steps, tables) follows for users + ranking.
 */
const Section = ({
  id,
  eyebrow,
  title,
  answer,
  children,
}: {
  id: string;
  eyebrow?: string;
  title: string;
  answer?: ReactNode;
  children: ReactNode;
}) => (
  <section
    id={id}
    className="scroll-mt-24 pt-12 mt-12 border-t border-border first:pt-0 first:mt-0 first:border-0"
  >
    {eyebrow && (
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-2">
        {eyebrow}
      </p>
    )}
    <h2 className="font-heading text-[1.625rem] lg:text-[2rem] font-bold text-navy mb-4 leading-[1.15] [text-wrap:balance]">
      {title}
    </h2>
    {answer && (
      <p className="speakable text-foreground/90 leading-[1.7] text-[17px] mb-6 border-l-2 border-gold pl-4 italic">
        {answer}
      </p>
    )}
    {children}
  </section>
);

/**
 * Inline conversion CTA — placed mid-content so high-intent readers can
 * convert without scrolling to the end of long-form pages.
 */
const InlineCTA = ({ label }: { label: string }) => (
  <div className="my-10 rounded-lg border border-gold/40 bg-gold/5 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
    <div className="min-w-0">
      <p className="font-heading font-bold text-navy text-lg leading-snug mb-1">{label}</p>
      <p className="text-foreground/75 text-[14px] leading-relaxed">
        Free, confidential 15-minute case review with a CPA — no sales pressure, no obligation.
      </p>
    </div>
    <div className="flex flex-col sm:flex-row gap-2 sm:flex-shrink-0">
      <a
        href={`tel:${brand.phoneTel}`}
        className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark text-navy font-bold px-5 py-3 rounded text-sm whitespace-nowrap"
      >
        <Phone className="w-4 h-4" /> {brand.phone}
      </a>
      <Link
        to="/contact"
        className="inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-dark text-white font-semibold px-5 py-3 rounded text-sm whitespace-nowrap"
      >
        Free Case Review <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </div>
);

const ServiceDetailPage = () => {
  const { categorySlug = "", serviceSlug = "" } = useParams();
  const found = getServiceDetail(categorySlug, serviceSlug);
  if (!found) return <Navigate to="/services" replace />;
  const { category, service } = found;
  const story = pickStoryForService(categorySlug, serviceSlug);
  const rich = getServiceRich(service.slug);

  const related = category.services.filter((s) => s.slug !== service.slug).slice(0, 3);
  const allFaqs = [...service.faqs, ...(rich.extendedFaqs ?? [])];

  // Prefer the richer mechanism steps when present; otherwise use the base process.
  const mechanismSteps =
    rich.howItWorks && rich.howItWorks.length > 0
      ? rich.howItWorks
      : service.process.map((p) => ({ title: p.title, body: p.description }));

  // Multi-schema JSON-LD: Service + FAQPage + HowTo + Speakable
  const serviceUrl = `${brand.siteUrl}/services/${category.slug}/${service.slug}`;
  const jsonLd: Record<string, unknown>[] = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${serviceUrl}#service`,
      name: service.title,
      serviceType: service.title,
      description: service.summary,
      url: serviceUrl,
      provider: {
        "@type": "Organization",
        name: brand.legalName,
        url: brand.siteUrl,
        telephone: brand.phoneTel,
      },
      areaServed: { "@type": "Country", name: "United States" },
      audience: { "@type": "Audience", audienceType: "Taxpayers with IRS problems" },
      offers: {
        "@type": "Offer",
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        description: "Free initial case consultation",
      },
    },
  ];

  if (allFaqs.length > 0) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: allFaqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: [".speakable", "h1"],
      },
    });
  }

  if (mechanismSteps.length > 0) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: `How ${service.title} Works`,
      description: service.summary,
      step: mechanismSteps.map((s, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: s.title,
        text: s.body,
      })),
    });
  }

  // Compact in-page TOC reflects the new direct-response order.
  const toc: { id: string; label: string }[] = [
    { id: "answer", label: `What is ${service.title.toLowerCase()}?` },
    rich.stats && { id: "proof", label: "Does it actually work?" },
    { id: "how-it-works", label: "How does the process work?" },
    rich.timeline && { id: "timeline", label: "How long does it take?" },
    { id: "fit", label: "Do I qualify?" },
    rich.costFactors && { id: "cost", label: "How much does it cost?" },
    rich.mistakes && { id: "mistakes", label: "What mistakes should I avoid?" },
    rich.alternatives && { id: "alternatives", label: "What are my alternatives?" },
    allFaqs.length > 0 && { id: "faqs", label: "More questions answered" },
    { id: "why-ctr", label: `Why hire ${brand.shortName}?` },
    rich.citations && { id: "sources", label: "Sources & references" },
  ].filter(Boolean) as { id: string; label: string }[];

  return (
    <SiteLayout>
      <PageSEO
        title={service.meta.title}
        description={service.meta.description}
        path={`/services/${category.slug}/${service.slug}`}
        image={stockImages[serviceHeroImage[service.slug] ?? "taxFormsCalculator"].src}
        breadcrumbs={[
          { name: "Home", href: "/" },
          { name: "Services", href: "/services" },
          { name: category.title, href: `/services/${category.slug}` },
          { name: service.title, href: `/services/${category.slug}/${service.slug}` },
        ]}
        jsonLd={jsonLd}
      />

      {/* 1. Hero — problem + promise + CTA */}
      <section className="relative bg-navy-dark text-white overflow-hidden">
        <HeroBackground
          image={stockImages[serviceHeroImage[service.slug] ?? "taxFormsCalculator"]}
          priority
        />
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12 lg:py-28 max-w-6xl">
          <Breadcrumbs
            items={[
              { name: "Home", href: "/" },
              { name: "Services", href: "/services" },
              { name: category.title, href: `/services/${category.slug}` },
              { name: service.title },
            ]}
            tone="dark"
          />
          <p className="text-[11px] lg:text-xs font-bold uppercase tracking-[0.15em] text-gold mt-3 lg:mt-2 mb-2 lg:mb-4">
            {category.title}
          </p>
          <h1 className="font-heading text-[1.875rem] sm:text-4xl lg:text-6xl font-bold leading-[1.05] mb-4 lg:mb-6 [text-wrap:balance]">
            {service.title}
          </h1>
          <p className="text-lg lg:text-xl text-white/90 mb-3 lg:mb-4 max-w-2xl leading-snug [text-wrap:balance]">
            {service.tagline}
          </p>
          <p className="text-white/80 max-w-2xl leading-relaxed mb-6 lg:mb-8">{service.hero}</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={`tel:${brand.phoneTel}`}
              className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark text-navy font-bold px-6 py-3.5 rounded"
            >
              <Phone className="w-4 h-4" /> Call {brand.phone}
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3.5 rounded border border-white/20"
            >
              Free Case Review <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <TrustStrip tone="dark" className="mt-8 pt-6 border-t border-white/10" />
        </div>
      </section>

      {/* 2. Urgency band — slim, single-style loss aversion */}
      {rich.urgency && (
        <section className="bg-gold/10 border-b border-gold/30">
          <div className="container mx-auto px-4 py-6 max-w-5xl">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-gold flex-shrink-0 mt-1" />
              <div>
                <p className="font-heading text-base lg:text-lg font-bold text-navy leading-snug mb-1.5">
                  {rich.urgency.headline}
                </p>
                <ul className="space-y-1 text-foreground/80 text-[14px] leading-relaxed">
                  {rich.urgency.points.map((p) => (
                    <li key={p} className="flex gap-2">
                      <span className="text-gold font-bold mt-0.5">•</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 3. Main body — single-rhythm direct response flow + sticky lead form */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="container mx-auto px-4 max-w-6xl grid lg:grid-cols-[minmax(0,1fr)_360px] gap-12 lg:gap-16 items-start">
          <article className="min-w-0">

            {/* Key takeaways — AEO answer box (the snippet AI engines lift first) */}
            {rich.quickAnswerBullets && rich.quickAnswerBullets.length > 0 && (
              <aside
                aria-label="Key takeaways"
                className="not-prose border-2 border-gold/40 rounded-lg bg-gold/5 p-5 lg:p-6 mb-6"
              >
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-2 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> Key takeaways
                </p>
                <p className="speakable font-heading text-navy text-[17px] leading-snug font-semibold mb-3">
                  {service.summary}
                </p>
                <ul className="space-y-1.5 text-[14px] text-foreground/85">
                  {rich.quickAnswerBullets.map((b) => (
                    <li key={b} className="flex gap-2 leading-relaxed">
                      <CheckCircle2 className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </aside>
            )}

            {/* On-page TOC — questions, not section labels */}
            <nav
              aria-label="Questions answered on this page"
              className="not-prose border border-border rounded-lg bg-muted/30 p-5 mb-2"
            >
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-foreground/60 mb-3">
                Questions answered on this page
              </p>
              <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                {toc.map((t, i) => (
                  <li key={t.id}>
                    <a href={`#${t.id}`} className="text-navy hover:text-gold inline-flex gap-2">
                      <span className="text-foreground/50 tabular-nums">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {t.label}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            {/* The short answer — direct definition for AI Overviews */}
            <Section
              id="answer"
              eyebrow="Direct answer"
              title={`What is ${service.title.toLowerCase()}?`}
              answer={allFaqs[0]?.a ?? service.summary}
            >
              <p className="text-foreground/85 leading-[1.75] text-[16px]">
                {service.hero}
              </p>
            </Section>

            {/* Proof — does it actually work? */}
            {rich.stats && (
              <Section
                id="proof"
                eyebrow="Proof"
                title={`Does ${service.title.toLowerCase()} actually work?`}
                answer={`Yes — here's what the IRS's own data shows about ${service.title.toLowerCase()} outcomes, timelines, and acceptance.`}
              >
                <div className="grid sm:grid-cols-3 gap-4">
                  {rich.stats.map((s) => (
                    <div key={s.label} className="border border-border rounded-lg p-5 bg-white">
                      <div className="font-heading text-3xl font-bold text-navy leading-none mb-2">
                        {s.value}
                      </div>
                      <div className="text-sm text-foreground/80 leading-snug mb-3">{s.label}</div>
                      <div className="text-[11px] text-foreground/55 leading-snug">
                        Source:{" "}
                        {s.sourceUrl ? (
                          <a
                            href={s.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:text-gold"
                          >
                            {s.source}
                          </a>
                        ) : (
                          s.source
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* How it works — process steps */}
            <Section
              id="how-it-works"
              eyebrow="Process"
              title={`How does the ${service.title.toLowerCase()} process work?`}
              answer={`In ${mechanismSteps.length} defined steps: ${mechanismSteps.map((s) => s.title.toLowerCase()).join(" → ")}. Most clients see protective action within days, with full resolution measured in weeks to months.`}
            >
              <ol className="space-y-6">
                {mechanismSteps.map((s, i) => (
                  <li key={s.title} className="flex gap-5">
                    <div className="font-heading text-2xl font-bold text-gold leading-none w-10 flex-shrink-0 tabular-nums">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-navy text-lg mb-1.5">{s.title}</h3>
                      <p className="text-foreground/85 leading-relaxed">{s.body}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </Section>

            <InlineCTA label={`Want to know if ${service.title.toLowerCase()} is right for your case?`} />

            {/* Timeline */}
            {rich.timeline && (
              <Section
                id="timeline"
                eyebrow="Timeline"
                title={`How long does ${service.title.toLowerCase()} take?`}
                answer={`Most cases move through ${rich.timeline.length} phases. Expect protective relief in the first phase and full resolution typically in ${rich.timeline[rich.timeline.length - 1]?.duration ?? "weeks to months"}.`}
              >
                <div className="border border-border rounded-lg divide-y divide-border overflow-hidden">
                  {rich.timeline.map((t, i) => (
                    <div
                      key={t.phase}
                      className="p-5 grid sm:grid-cols-[110px_140px_1fr] gap-4 items-start"
                    >
                      <div className="font-heading text-xl font-bold text-gold leading-none tabular-nums">
                        Phase {i + 1}
                      </div>
                      <div className="text-sm text-foreground/70 font-semibold">{t.duration}</div>
                      <div>
                        <p className="font-heading font-bold text-navy mb-1">{t.phase}</p>
                        <p className="text-foreground/80 text-[15px] leading-relaxed">{t.what}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Eligibility / fit */}
            <Section
              id="fit"
              eyebrow="Eligibility"
              title={`Do I qualify for ${service.title.toLowerCase()}?`}
              answer={`${service.title} fits a specific set of taxpayer situations — and there are cases where a different strategy will get you a better outcome. Here's exactly who it's for and who it isn't.`}
            >
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
                <div>
                  <h3 className="font-heading text-base font-bold text-navy mb-3 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-gold" /> Who this is for
                  </h3>
                  <ul className="space-y-2.5">
                    {service.whoItsFor.map((w) => (
                      <li
                        key={w}
                        className="flex items-start gap-2 text-foreground/85 text-[15px]"
                      >
                        <CheckCircle2 className="w-4 h-4 text-gold mt-1 flex-shrink-0" /> {w}
                      </li>
                    ))}
                  </ul>
                </div>
                {rich.disqualifiers && (
                  <div>
                    <h3 className="font-heading text-base font-bold text-navy mb-3 flex items-center gap-2">
                      <XCircle className="w-5 h-5 text-foreground/60" /> When this isn't the right tool
                    </h3>
                    <ul className="space-y-2.5">
                      {rich.disqualifiers.map((d) => (
                        <li
                          key={d}
                          className="flex items-start gap-2 text-foreground/75 text-[15px]"
                        >
                          <XCircle className="w-4 h-4 text-foreground/50 mt-1 flex-shrink-0" /> {d}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              {service.whatWeDo.length > 0 && (
                <div className="mt-8 pt-6 border-t border-border">
                  <h3 className="font-heading text-base font-bold text-navy mb-3">
                    What we handle for you
                  </h3>
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
                    {service.whatWeDo.map((w) => (
                      <li
                        key={w}
                        className="flex items-start gap-2 text-foreground/85 text-[15px]"
                      >
                        <CheckCircle2 className="w-4 h-4 text-gold mt-1 flex-shrink-0" /> {w}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Section>

            {/* Cost */}
            {rich.costFactors && (
              <Section
                id="cost"
                eyebrow="Investment"
                title={`How much does ${service.title.toLowerCase()} cost?`}
                answer="We engage on a written flat fee with no hourly billing — typical engagements range from a few thousand to mid-five figures depending on the factors below. Your initial consultation is free and you receive a written quote before any commitment."
              >
                <p className="text-foreground/85 mb-4 text-[16px] leading-relaxed">
                  The fee for your case depends on:
                </p>
                <ul className="space-y-2">
                  {rich.costFactors.map((c) => (
                    <li
                      key={c}
                      className="flex items-start gap-2 text-foreground/85 text-[15px]"
                    >
                      <span className="text-gold font-bold mt-0.5">•</span> {c}
                    </li>
                  ))}
                </ul>
                <p className="text-foreground/70 text-sm mt-4 italic">
                  Your initial consultation is free. You'll get a written scope and flat-fee quote
                  before any engagement.
                </p>
              </Section>
            )}

            {/* Mistakes — objection handling */}
            {rich.mistakes && (
              <Section
                id="mistakes"
                eyebrow="Watch out"
                title={`What mistakes should I avoid with ${service.title.toLowerCase()}?`}
                answer="These are the four most common errors we see when taxpayers (or under-qualified preparers) handle these cases — each one can cost months of delay or trigger an outright rejection."
              >
                <div className="space-y-5">
                  {rich.mistakes.map((m) => (
                    <div key={m.title} className="border-l-2 border-gold/60 pl-5">
                      <h3 className="font-heading font-bold text-navy mb-1.5">{m.title}</h3>
                      <p className="text-foreground/80 leading-relaxed text-[15px]">{m.body}</p>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Alternatives */}
            {rich.alternatives && (
              <Section
                id="alternatives"
                eyebrow="Compare"
                title={`What are the alternatives to ${service.title.toLowerCase()}?`}
                answer={`${service.title} isn't the only path. Here are the other IRS programs that may fit better depending on your finances, asset picture, and timeline.`}
              >
                <div className="border border-border rounded-lg divide-y divide-border overflow-hidden">
                  {rich.alternatives.map((a) => (
                    <div key={a.name} className="p-5 sm:grid sm:grid-cols-[1fr_2fr] gap-6">
                      <p className="font-heading font-bold text-navy mb-1 sm:mb-0">{a.name}</p>
                      <p className="text-foreground/80 text-[15px] leading-relaxed">{a.whenItFits}</p>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            <InlineCTA label={`Not sure which path fits your situation? We'll tell you straight.`} />

            {/* FAQs — moved up: Q&A is the most-extracted format by AI engines */}
            {allFaqs.length > 0 && (
              <Section
                id="faqs"
                eyebrow="More questions answered"
                title={`Common questions about ${service.title.toLowerCase()}`}
                answer={`Direct answers to the ${allFaqs.length} questions we hear most often from taxpayers researching ${service.title.toLowerCase()}.`}
              >
                <div className="divide-y divide-border border-y border-border">
                  {allFaqs.map((f, i) => (
                    <details key={f.q} className="group py-5" open={i < 2}>
                      <summary className="font-heading font-semibold text-navy cursor-pointer list-none flex justify-between items-center gap-4 text-[16px]">
                        {f.q}
                        <span className="text-gold group-open:rotate-45 transition-transform text-2xl leading-none flex-shrink-0">
                          +
                        </span>
                      </summary>
                      <p className="text-foreground/80 mt-4 leading-relaxed">{f.a}</p>
                    </details>
                  ))}
                </div>
              </Section>
            )}

            {/* Why CTR — credibility */}
            <Section
              id="why-ctr"
              eyebrow={`Why ${brand.shortName}`}
              title={`Why should I hire ${brand.shortName} for ${service.title.toLowerCase()}?`}
              answer={`Your case is reviewed and led by ${founder.name}, ${founder.credentials} — backed by a flat-fee engagement, same-day Power of Attorney filing, and 16+ years of direct IRS representation.`}
            >
              <ul className="space-y-3 mb-5">
                {(rich.whyCTR ?? []).map((w) => (
                  <li
                    key={w}
                    className="flex items-start gap-3 text-foreground/85 leading-relaxed text-[15px]"
                  >
                    <ShieldCheck className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" /> {w}
                  </li>
                ))}
              </ul>
              <Link
                to="/about/franklin-sofi"
                className="inline-flex items-center gap-1 font-semibold text-navy hover:text-gold"
              >
                Read Franklin's full bio <ArrowRight className="w-4 h-4" />
              </Link>
            </Section>


            {/* Sources */}
            {rich.citations && (
              <Section id="sources" eyebrow="References" title="Sources & further reading">
                <p className="text-foreground/70 text-sm mb-4 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-gold" />
                  Authoritative primary sources cited or referenced on this page.
                </p>
                <ul className="space-y-2">
                  {rich.citations.map((c) => (
                    <li key={c.url}>
                      <a
                        href={c.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-navy hover:text-gold inline-flex items-center gap-1.5 text-[15px] underline underline-offset-2"
                      >
                        {c.label} <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {/* Related */}
            {related.length > 0 && (
              <div className="mt-12 pt-8 border-t border-border">
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-4">
                  Related services
                </p>
                <ul className="space-y-2">
                  {related.map((r) => (
                    <li key={r.slug}>
                      <Link
                        to={`/services/${category.slug}/${r.slug}`}
                        className="text-navy hover:text-gold font-semibold inline-flex items-center gap-1"
                      >
                        {r.title} <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </article>

          <ServiceLeadForm source={service.slug} serviceLabel={service.title} />
        </div>
      </section>

      {/* 4. Client story */}
      <ClientStoryBlock story={story} eyebrow="A real case we resolved" />

      <CTASection />
    </SiteLayout>
  );
};

// silence unused warning — categories list retained for potential future nav
void serviceCategories;

export default ServiceDetailPage;
